import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Search, CheckCircle, Clock, AlertCircle, Wrench } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicStatusCheck = () => {
  const [ticketNumber, setTicketNumber] = useState('');
  const [ticketInfo, setTicketInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!ticketNumber.trim()) {
      setError('Ju lutem vendosni numrin e tiketës');
      return;
    }

    setLoading(true);
    setError('');
    setTicketInfo(null);

    try {
      const response = await axios.get(`${API}/public/status/${ticketNumber.trim()}`);
      setTicketInfo(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Tiketa nuk u gjet. Kontrolloni numrin e tiketës.');
      } else {
        setError('Gabim në kontrollimin e statusit. Ju lutem provoni përsëri.');
      }
      console.error('Status check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (statusKey) => {
    switch(statusKey) {
      case 'gati': return <CheckCircle className="w-5 h-5" />;
      case 'perfunduar': return <CheckCircle className="w-5 h-5" />;
      case 'ne_process': return <Wrench className="w-5 h-5" />;
      case 'ne_pritje': return <Clock className="w-5 h-5" />;
      default: return <AlertCircle className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            IT Solution
          </h1>
          <p className="text-slate-600 text-lg">
            Kontrolloni Statusin e Tiketës Tuaj
          </p>
        </div>

        {/* Search Card */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Search className="w-6 h-6 mr-2 text-blue-600" />
              Kërkimi i Tiketës
            </CardTitle>
            <CardDescription>
              Vendosni numrin e tiketës për të parë statusin aktual
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <Label htmlFor="ticket-number">Numri i Tiketës</Label>
                <Input
                  id="ticket-number"
                  type="text"
                  placeholder="p.sh: IT2511251"
                  value={ticketNumber}
                  onChange={(e) => setTicketNumber(e.target.value)}
                  className="text-lg"
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">
                  Numri i tiketës gjendet në faturën tuaj
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Duke kërkuar...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Kontrollo Statusin
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results Card */}
        {ticketInfo && (
          <Card className="mt-6 shadow-xl animate-fadeIn">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">
              <CardTitle className="text-2xl">
                Tiketa #{ticketInfo.ticket_number}
              </CardTitle>
              <CardDescription className="text-blue-100">
                Informacioni i Tiketës Tuaj
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Status */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-slate-600">Statusi Aktual:</span>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge className={`${ticketInfo.status_info?.color || 'bg-slate-500'} text-white text-lg py-2 px-4`}>
                        {getStatusIcon(ticketInfo.statusi)}
                        <span className="ml-2">{ticketInfo.status_info?.label || ticketInfo.statusi}</span>
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Client & Device Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-slate-600">Klienti:</span>
                  <p className="text-lg font-semibold text-slate-900">{ticketInfo.client_emri}</p>
                </div>
                {ticketInfo.paisja && (
                  <div>
                    <span className="text-sm font-medium text-slate-600">Paisja:</span>
                    <p className="text-lg font-semibold text-slate-900">{ticketInfo.paisja}</p>
                  </div>
                )}
                {ticketInfo.marka && (
                  <div>
                    <span className="text-sm font-medium text-slate-600">Marka:</span>
                    <p className="text-slate-900">{ticketInfo.marka}</p>
                  </div>
                )}
                {ticketInfo.modeli && (
                  <div>
                    <span className="text-sm font-medium text-slate-600">Modeli:</span>
                    <p className="text-slate-900">{ticketInfo.modeli}</p>
                  </div>
                )}
              </div>

              {/* Problem Description */}
              {ticketInfo.pershkrimi_problemit && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <span className="text-sm font-medium text-slate-600 block mb-2">Përshkrimi i Problemit:</span>
                  <p className="text-slate-800">{ticketInfo.pershkrimi_problemit}</p>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ticketInfo.data_regjistrimit && (
                  <div>
                    <span className="text-sm font-medium text-slate-600">Data e Regjistrimit:</span>
                    <p className="text-slate-900">
                      {new Date(ticketInfo.data_regjistrimit).toLocaleDateString('sq-AL')}
                    </p>
                  </div>
                )}
                {ticketInfo.data_perfundimit && (
                  <div>
                    <span className="text-sm font-medium text-slate-600">Data e Përfundimit:</span>
                    <p className="text-slate-900 font-semibold text-green-600">
                      {new Date(ticketInfo.data_perfundimit).toLocaleDateString('sq-AL')}
                    </p>
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Keni pyetje?</strong> Kontaktoni në: <span className="font-mono">+383 XX XXX XXX</span>
                </p>
              </div>

              {/* New Search Button */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setTicketInfo(null);
                  setTicketNumber('');
                  setError('');
                }}
              >
                Kërko Tiketë Tjetër
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-slate-600 text-sm">
          <p>© 2025 IT Solution - Menaxhimi i Servisimit</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PublicStatusCheck;
